import React, { useState } from 'react';
import { FaPlus, FaTrash, FaGripVertical, FaChevronDown, FaChevronUp, FaInfoCircle } from 'react-icons/fa';

// Common feature icons (using FontAwesome icon names)
const FEATURE_ICONS = [
  { value: null, label: 'None', icon: null },
  { value: 'wifi', label: 'WiFi', icon: '📶' },
  { value: 'tv', label: 'TV/Streaming', icon: '📺' },
  { value: 'speed', label: 'Speed', icon: '⚡' },
  { value: 'shield', label: 'Security', icon: '🛡️' },
  { value: 'infinity', label: 'Unlimited', icon: '∞' },
  { value: 'support', label: 'Support', icon: '💬' },
  { value: 'devices', label: 'Multiple Devices', icon: '📱' },
  { value: 'gaming', label: 'Gaming', icon: '🎮' },
  { value: 'office', label: 'Home Office', icon: '💼' },
  { value: 'cloud', label: 'Cloud Storage', icon: '☁️' },
  { value: 'streaming', label: '4K Streaming', icon: '🎬' }
];

const FE_FIELD =
  'w-full rounded-lg border border-[#504945] bg-[#3c3836] px-3 py-2 text-sm text-[#fbf1c7] placeholder:text-[#928374] focus:border-[#83a598] focus:outline-none focus:ring-2 focus:ring-[#83a598]';

// Feature categories
const FEATURE_CATEGORIES = [
  { value: null, label: 'None' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'speed', label: 'Speed & Performance' },
  { value: 'support', label: 'Support & Service' },
  { value: 'security', label: 'Security & Privacy' },
  { value: 'business', label: 'Business Features' },
  { value: 'data', label: 'Data & Storage' }
];

const FeatureEditor = ({ features = [], onChange, error = '' }) => {
  const [expandedFeatures, setExpandedFeatures] = useState(new Set());
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Normalize features - handle both string and object formats
  const normalizeFeature = (feature) => {
    if (typeof feature === 'string') {
      return {
        id: `feature-${Date.now()}-${Math.random()}`,
        title: feature.trim(),
        description: '',
        icon: null,
        category: null,
        order: 0
      };
    }
    return {
      id: feature.id || `feature-${Date.now()}-${Math.random()}`,
      title: feature.title || '',
      description: feature.description || '',
      icon: feature.icon || null,
      category: feature.category || null,
      order: feature.order || 0
    };
  };

  const normalizedFeatures = features.map((f, index) => {
    const normalized = normalizeFeature(f);
    if (typeof normalized.order !== 'number') {
      normalized.order = index;
    }
    return normalized;
  }).sort((a, b) => a.order - b.order);

  const handleAddFeature = () => {
    const newFeature = {
      id: `feature-${Date.now()}-${Math.random()}`,
      title: '',
      description: '',
      icon: null,
      category: null,
      order: normalizedFeatures.length
    };
    onChange([...normalizedFeatures, newFeature]);
    // Auto-expand new feature for editing
    setExpandedFeatures(new Set([...expandedFeatures, newFeature.id]));
  };

  const handleRemoveFeature = (index) => {
    const updated = normalizedFeatures.filter((_, i) => i !== index);
    // Reorder remaining features
    const reordered = updated.map((f, i) => ({ ...f, order: i }));
    onChange(reordered);
  };

  const handleFeatureChange = (index, field, value) => {
    const updated = [...normalizedFeatures];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const toggleExpand = (featureId) => {
    const newExpanded = new Set(expandedFeatures);
    if (newExpanded.has(featureId)) {
      newExpanded.delete(featureId);
    } else {
      newExpanded.add(featureId);
    }
    setExpandedFeatures(newExpanded);
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const draggedFeature = normalizedFeatures[draggedIndex];
    const updated = [...normalizedFeatures];
    updated.splice(draggedIndex, 1);
    updated.splice(index, 0, draggedFeature);
    
    // Update order values
    const reordered = updated.map((f, i) => ({ ...f, order: i }));
    onChange(reordered);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-[#bdae93]">
          Features *
          <span className="ml-2 text-xs text-[#928374] font-normal">({normalizedFeatures.length} feature{normalizedFeatures.length !== 1 ? 's' : ''})</span>
        </label>
        <button
          type="button"
          onClick={handleAddFeature}
          className="inline-flex items-center rounded-lg bg-[#504945] px-3 py-1.5 text-sm font-medium text-[#83a598] transition-colors hover:bg-[#665c54]"
        >
          <FaPlus className="mr-1.5" size={12} />
          Add Feature
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2 flex items-center">
          <FaInfoCircle className="mr-2" />
          {error}
        </div>
      )}

      {normalizedFeatures.length === 0 ? (
        <div className="text-center py-8 text-[#928374] bg-[#32302f] rounded-lg border-2 border-dashed border-[#504945]">
          <p className="text-sm">No features added yet.</p>
          <p className="text-xs mt-1">Click "Add Feature" to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {normalizedFeatures.map((feature, index) => {
            const isExpanded = expandedFeatures.has(feature.id);
            const isDragging = draggedIndex === index;

            return (
              <div
                key={feature.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`bg-[#282828] border-2 rounded-lg transition-all ${
                  isDragging
                    ? 'border-[#83a598] shadow-lg opacity-50'
                    : 'border-[#504945] hover:border-[#665c54]'
                }`}
              >
                <div className="p-3">
                  <div className="flex items-center space-x-3">
                    {/* Drag Handle */}
                    <div className="cursor-move text-gray-400 hover:text-[#bdae93]">
                      <FaGripVertical />
                    </div>

                    {/* Feature Icon Preview */}
                    {feature.icon && (
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg text-lg">
                        {FEATURE_ICONS.find(i => i.value === feature.icon)?.icon || '📌'}
                      </div>
                    )}

                    {/* Feature Title */}
                    <div className="flex-1">
                      <input
                        type="text"
                        value={feature.title || ''}
                        onChange={(e) => handleFeatureChange(index, 'title', e.target.value)}
                        placeholder="Feature title (e.g., 4K Streaming)"
                        className={`${FE_FIELD} font-medium`}
                      />
                    </div>

                    {/* Expand/Collapse Button */}
                    <button
                      type="button"
                      onClick={() => toggleExpand(feature.id)}
                      className="p-2 text-gray-400 hover:text-[#bdae93] rounded-lg hover:bg-gray-100"
                      title={isExpanded ? 'Collapse' : 'Expand for more options'}
                    >
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </button>

                    {/* Remove Button */}
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(index)}
                      className="p-2 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                      title="Remove feature"
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>

                  {/* Expanded Options */}
                  {isExpanded && (
                    <div className="mt-3 space-y-3 border-t border-[#504945] pt-3">
                      <div>
                        <label className="block text-xs font-medium text-[#bdae93] mb-1">
                          Description (Optional)
                        </label>
                        <textarea
                          value={feature.description || ''}
                          onChange={(e) => handleFeatureChange(index, 'description', e.target.value)}
                          placeholder="Add more details about this feature..."
                          rows="2"
                          className={FE_FIELD}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-[#bdae93] mb-1">
                            Icon
                          </label>
                          <select
                            value={feature.icon || ''}
                            onChange={(e) => handleFeatureChange(index, 'icon', e.target.value || null)}
                            className={FE_FIELD}
                          >
                            {FEATURE_ICONS.map(icon => (
                              <option key={icon.value || 'none'} value={icon.value || ''}>
                                {icon.icon ? `${icon.icon} ${icon.label}` : icon.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-[#bdae93] mb-1">
                            Category
                          </label>
                          <select
                            value={feature.category || ''}
                            onChange={(e) => handleFeatureChange(index, 'category', e.target.value || null)}
                            className={FE_FIELD}
                          >
                            {FEATURE_CATEGORIES.map(cat => (
                              <option key={cat.value || 'none'} value={cat.value || ''}>
                                {cat.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Validation Error */}
                  {!feature.title?.trim() && (
                    <div className="mt-2 text-xs text-red-600">
                      Feature title is required
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Feature Templates Quick Add */}
      {normalizedFeatures.length > 0 && (
        <div className="pt-2 border-t border-gray-200">
          <details className="group">
            <summary className="text-sm font-medium text-[#bdae93] cursor-pointer hover:text-[#ebdbb2] list-none">
              <span className="flex items-center">
                <FaInfoCircle className="mr-2" />
                Quick Add Common Features
              </span>
            </summary>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
              {[
                { title: 'Unlimited Data', icon: 'infinity', category: 'data' },
                // Was '24/7 Support' — a one-click way for an admin to publish a
                // claim we cannot honour. Support is Mon–Fri 8am–6pm AEST.
                { title: 'Australian Support', icon: 'support', category: 'support' },
                { title: 'No Lock-in Contract', icon: 'shield', category: 'security' },
                { title: '4K Streaming', icon: 'streaming', category: 'entertainment' },
                { title: 'Multiple Devices', icon: 'devices', category: 'speed' },
                { title: 'Priority Support', icon: 'support', category: 'support' }
              ].map((template, idx) => {
                const exists = normalizedFeatures.some(f => f.title.toLowerCase() === template.title.toLowerCase());
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      if (!exists) {
                        const newFeature = {
                          id: `feature-${Date.now()}-${Math.random()}`,
                          title: template.title,
                          description: '',
                          icon: template.icon,
                          category: template.category,
                          order: normalizedFeatures.length
                        };
                        onChange([...normalizedFeatures, newFeature]);
                      }
                    }}
                    disabled={exists}
                    className={`text-xs px-3 py-2 rounded-lg border transition-colors ${
                      exists
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-[#282828] text-[#bdae93] border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    {FEATURE_ICONS.find(i => i.value === template.icon)?.icon || '📌'} {template.title}
                  </button>
                );
              })}
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default FeatureEditor;

